export type SparkleId = 'left' | 'right';

export interface SparkleTimeline {
	keyTimes: string;
	values: string;
}

export interface SparkleScaleTimeline extends SparkleTimeline {
	base: string;
}

export interface SparkleConfig {
	id: SparkleId;
	groupTransform: string;
	opacity: SparkleTimeline;
	translate: SparkleTimeline;
	scale: SparkleScaleTimeline;
	idleDisplay: 'stroke' | 'filled';
	idlePath: string;
	animatedPathBase: string;
	animatedPath: SparkleTimeline;
	animatedStrokePath?: string;
}

// If you like magic numbers, you'll love this wizardry 🧙

const sparkleOuterPath =
	'M 11.017 2.814 A 1 1 0 0 1 12.983 2.814 L 14.034 8.372 A 2 2 0 0 0 15.628 9.966 L 21.186 11.017 A 1 1 0 0 1 21.186 12.983 L 15.628 14.034 A 2 2 0 0 0 14.034 15.628 L 12.983 21.186 A 1 1 0 0 1 11.017 21.186 L 9.966 15.628 A 2 2 0 0 0 8.372 14.034 L 2.814 12.983 A 1 1 0 0 1 2.814 11.017 L 8.372 9.966 A 2 2 0 0 0 9.966 8.372 Z';
const sparkleStrokePath =
	'M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z';
const sparkleCollapsedInnerPath =
	'M 12 12 A 0 0 0 0 1 12 12 L 12 12 A 0 0 0 0 0 12 12 L 12 12 A 0 0 0 0 1 12 12 L 12 12 A 0 0 0 0 0 12 12 L 12 12 A 0 0 0 0 1 12 12 L 12 12 A 0 0 0 0 0 12 12 L 12 12 A 0 0 0 0 1 12 12 L 12 12 A 0 0 0 0 0 12 12 Z';
const largeSparklePath = `${sparkleOuterPath} M 11.332 5.754 A 0.68 0.68 0 0 1 12.668 5.754 L 13.383 9.533 A 1.36 1.36 0 0 0 14.467 10.617 L 18.246 11.332 A 0.68 0.68 0 0 1 18.246 12.668 L 14.467 13.383 A 1.36 1.36 0 0 0 13.383 14.467 L 12.668 18.246 A 0.68 0.68 0 0 1 11.332 18.246 L 10.617 14.467 A 1.36 1.36 0 0 0 9.533 13.383 L 5.754 12.668 A 0.68 0.68 0 0 1 5.754 11.332 L 9.533 10.617 A 1.36 1.36 0 0 0 10.617 9.533 Z`;
const smallSparklePath = `${sparkleOuterPath} M 11.273 5.202 A 0.74 0.74 0 0 1 12.727 5.202 L 13.505 9.315 A 1.48 1.48 0 0 0 14.685 10.495 L 18.798 11.273 A 0.74 0.74 0 0 1 18.798 12.727 L 14.685 13.505 A 1.48 1.48 0 0 0 13.505 14.685 L 12.727 18.798 A 0.74 0.74 0 0 1 11.273 18.798 L 10.495 14.685 A 1.48 1.48 0 0 0 9.315 13.505 L 5.202 12.727 A 0.74 0.74 0 0 1 5.202 11.273 L 9.315 10.495 A 1.48 1.48 0 0 0 10.495 9.315 Z`;
const smallSparkleLatePath = `${sparkleOuterPath} M 11.469 7.04 A 0.54 0.54 0 0 1 12.531 7.04 L 13.098 10.041 A 1.08 1.08 0 0 0 13.959 10.902 L 16.96 11.469 A 0.54 0.54 0 0 1 16.96 12.531 L 13.959 13.098 A 1.08 1.08 0 0 0 13.098 13.959 L 12.531 16.96 A 0.54 0.54 0 0 1 11.469 16.96 L 10.902 13.959 A 0.54 0.54 0 0 0 10.041 13.098 L 7.04 12.531 A 0.54 0.54 0 0 1 7.04 11.469 L 10.041 10.902 A 1.08 1.08 0 0 0 10.902 10.041 Z`;
const largeSparkleFilledPath = `${sparkleOuterPath} ${sparkleCollapsedInnerPath}`;
const smallSparkleFilledPath = `${sparkleOuterPath} ${sparkleCollapsedInnerPath}`;

export const sparkles: SparkleConfig[] = [
	{
		id: 'left',
		groupTransform: 'translate(-1 -1)',
		opacity: {
			keyTimes: '0;0.33;0.331;0.66;1',
			values: '1;0;0;1;1',
		},
		translate: {
			keyTimes: '0;0.33;0.331;1',
			values: '-1 -1;-1 -7;-1 8;-1 -1',
		},
		scale: {
			base: 'scale(0.58)',
			keyTimes: '0;0.33;0.331;1',
			values: '0.58 0.58;0.48 0.48;0.36 0.36;0.58 0.58',
		},
		idleDisplay: 'stroke',
		idlePath: sparkleStrokePath,
		animatedPathBase: largeSparklePath,
		animatedPath: {
			keyTimes: '0;0.33;0.331;1',
			values: `${largeSparklePath};${largeSparklePath};${largeSparkleFilledPath};${largeSparklePath}`,
		},
		animatedStrokePath: sparkleStrokePath,
	},
	{
		id: 'right',
		groupTransform: 'translate(5 6)',
		opacity: {
			keyTimes: '0;0.5;0.75;0.751;1',
			values: '1;1;0;0;1',
		},
		translate: {
			keyTimes: '0;0.5;0.75;0.751;1',
			values: '5 6;5 1;5 -4;5 8;5 6',
		},
		scale: {
			base: 'scale(0.26)',
			keyTimes: '0;0.5;0.75;0.751;1',
			values: '0.26 0.26;0.34 0.34;0.28 0.28;0.2 0.2;0.26 0.26',
		},
		idleDisplay: 'filled',
		idlePath: smallSparkleFilledPath,
		animatedPathBase: smallSparkleFilledPath,
		animatedPath: {
			keyTimes: '0;0.5;0.75;0.751;1',
			values: `${smallSparkleFilledPath};${smallSparklePath};${smallSparkleLatePath};${smallSparkleFilledPath};${smallSparkleFilledPath}`,
		},
	},
];
